<template>
  <div>
    <b-card class="health-assessments-list-card">
      <div class="gradient-line-wrapper"></div>
      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0">In-House Medical Records</h5>
          <small class="text-muted"
            >Manage and review student medical visits</small
          >
        </div>
      </div>

      <b-row class="align-items-center filter-row-single-line mb-2 no-wrap-row">
        <b-col xl="4" lg="4" md="5" sm="12" cols="12">
          <b-form-input
            v-model="searchQuery"
            placeholder="Search by student name, symptoms, medication, remarks..."
            @input="onSearchChange"
            class="filter-control"
            debounce="500"
          />
        </b-col>
        <b-col xl="4" lg="4" md="4" sm="12" cols="12">
          <div class="d-flex align-items-center gap-2">
            <flat-pickr
              v-model="filters.dateRange"
              :config="dateRangeConfig"
              class="form-control filter-control"
              placeholder="Date Range"
              @on-change="onDateRangeChange"
            />
            <b-button
              v-if="hasActiveFilters"
              variant="outline-secondary"
              @click="clearFilters"
              class="clear-btn-circle"
              v-b-tooltip.hover.top="'Clear Filters'"
            >
              <feather-icon icon="XIcon" size="16" />
            </b-button>
          </div>
        </b-col>
        <b-col xl="4" lg="4" md="3" sm="12" cols="12" class="text-right">
          <div
            class="d-flex align-items-center justify-content-end action-buttons-group"
          >
            <b-button
              variant="outline-primary"
              @click="openPrintModal"
              class="print-btn-icon"
            >
              <feather-icon icon="PrinterIcon" size="16" />
            </b-button>
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              @click="openAddModal"
              class="action-btn-filter"
              :disabled="loading"
            >
              <b-spinner v-if="loading" small class="mr-1" />
              <feather-icon v-else icon="PlusIcon" size="16" class="mr-1" />
              Add Record
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Pagination Controls - Top -->
      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-3 flex-wrap"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Pagination Switch -->
          <b-form-checkbox
            v-model="showPagination"
            switch
            size="sm"
            class="pagination-switch-compact"
          >
            <small class="text-muted">Pagination</small>
          </b-form-checkbox>

          <!-- Page Size Dropdown -->
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              :key="'page-size-' + perPage"
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
              @option:selected="closePageSizeDropdown"
              ref="pageSizeSelect"
            />
          </div>
        </div>

        <!-- Count Display -->
        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <!-- Pagination -->
        <div v-if="showPagination" class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @change="onPageChange"
          />
        </div>
      </div>

      <!-- Data Grid with Horizontal Scroll -->
      <div class="table-scroll-container">
        <div
          class="table-scrollbar-top"
          ref="topScrollbar"
          @scroll="syncScroll"
        >
          <div class="table-scrollbar-content"></div>
        </div>

        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
          <b-table
            class="health-assessments-table"
            :items="paginatedRecords"
            :fields="fields"
            :busy="dataLoading"
            :responsive="false"
            hover
            show-empty
            empty-text="No medical records found"
          >
            <template #table-busy>
              <tbody>
                <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                  <td
                    v-for="(field, index) in fields"
                    :key="'skeleton-cell-' + index"
                    class="skeleton-cell"
                  >
                    <div class="skeleton-loader"></div>
                  </td>
                </tr>
              </tbody>
            </template>

            <template #cell(sno)="{ index }">
              <span class="text-muted">{{
                (currentPage - 1) * perPage + index + 1
              }}</span>
            </template>

            <template #cell(date)="{ item }">
              <span>{{ formatDate(item.med && item.med.date) }}</span>
            </template>

            <template #cell(studentInfo)="{ item }">
              <div>
                <div class="font-weight-bold">
                  {{ (item.std && item.std.name) || "N/A" }}
                </div>
                <div class="text-muted small">
                  {{
                    (item.std &&
                      item.std.cls_current &&
                      item.std.cls_current.name) ||
                    "N/A"
                  }}
                </div>
              </div>
            </template>

            <template #cell(reportedBy)="{ item }">
              <span>{{ (item.med && item.med.reportedBy) || "N/A" }}</span>
            </template>

            <template #cell(symptoms)="{ item }">
              <div
                class="text-truncate"
                style="max-width: 150px"
                :title="(item.med && item.med.symptoms) || 'N/A'"
              >
                {{ (item.med && item.med.symptoms) || "N/A" }}
              </div>
            </template>

            <template #cell(actionTakenAtSchool)="{ item }">
              <div
                class="text-truncate"
                style="max-width: 150px"
                :title="(item.med && item.med.actionTakenAtSchool) || 'N/A'"
              >
                {{ (item.med && item.med.actionTakenAtSchool) || "N/A" }}
              </div>
            </template>

            <template #cell(medicationGiven)="{ item }">
              <div
                class="text-truncate"
                style="max-width: 120px"
                :title="(item.med && item.med.medicationGiven) || 'N/A'"
              >
                {{ (item.med && item.med.medicationGiven) || "N/A" }}
              </div>
            </template>

            <template #cell(observation)="{ item }">
              <div
                class="text-truncate"
                style="max-width: 120px"
                :title="(item.med && item.med.observation) || 'N/A'"
              >
                {{ (item.med && item.med.observation) || "N/A" }}
              </div>
            </template>

            <template #cell(parentActionRequired)="{ item }">
              <b-badge
                :variant="
                  item.med && item.med.parentActionRequired === 'Yes'
                    ? 'danger'
                    : 'success'
                "
              >
                {{ (item.med && item.med.parentActionRequired) || "N/A" }}
              </b-badge>
            </template>

            <template #cell(remarks)="{ item }">
              <span>{{ (item.med && item.med.remarks) || "N/A" }}</span>
            </template>

            <template #cell(actions)="{ item }">
              <div class="d-flex align-items-center justify-content-end">
                <b-button
                  variant="gradient-primary"
                  size="sm"
                  v-b-tooltip.hover.left
                  title="View Details"
                  class="action-btn-table mr-1"
                  @click="viewRecord(item)"
                  :disabled="
                    isActionLoading(item, 'view') ||
                    isActionLoading(item, 'edit') ||
                    isActionLoading(item, 'delete')
                  "
                >
                  <b-spinner
                    v-if="isActionLoading(item, 'view')"
                    small
                    type="grow"
                  />
                  <feather-icon v-else icon="EyeIcon" size="16" />
                </b-button>

                <b-button
                  v-if="rights.edit"
                  variant="gradient-primary"
                  size="sm"
                  v-b-tooltip.hover.left
                  title="Edit Record"
                  class="action-btn-table mr-1"
                  @click="editRecord(item)"
                  :disabled="
                    isActionLoading(item, 'edit') ||
                    isActionLoading(item, 'delete')
                  "
                >
                  <b-spinner
                    v-if="isActionLoading(item, 'edit')"
                    small
                    type="grow"
                  />
                  <feather-icon v-else icon="EditIcon" size="16" />
                </b-button>

                <b-button
                  v-if="rights.delete"
                  variant="outline-danger"
                  size="sm"
                  v-b-tooltip.hover.right
                  title="Delete Record"
                  class="action-btn-outline"
                  @click="deleteRecord(item)"
                  :disabled="
                    isActionLoading(item, 'delete') ||
                    isActionLoading(item, 'edit')
                  "
                >
                  <b-spinner
                    v-if="isActionLoading(item, 'delete')"
                    small
                    type="grow"
                  />
                  <feather-icon v-else icon="Trash2Icon" size="16" />
                </b-button>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Medical Record' : 'Add Medical Record'"
      size="lg"
      @hidden="resetForm"
      centered
      no-close-on-backdrop
    >
      <b-form @submit.prevent class="mt-2">
        <div class="form-card soft-card">
          <b-row>
            <b-col md="6">
              <b-form-group label="Date" label-for="date" required>
                <flat-pickr
                  v-model="form.date"
                  :config="dateConfig"
                  class="form-control input-compact"
                  :placeholder="$t('Select Date')"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Time" label-for="time" required>
                <flat-pickr
                  v-model="form.time"
                  :config="timeConfig"
                  class="form-control input-compact"
                  placeholder="Select time"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <hr class="my-2" />

        <div class="form-card soft-card">
          <b-row class="align-items-end gutter-tight">
            <b-col md="6" class="pr-md-1">
              <b-form-group label="Student" label-for="student" required>
                <div class="search-section">
                  <b-form-input
                    v-model="studentSearchQuery"
                    placeholder="Search students..."
                    @input="onStudentSearchChange"
                    class="search-input input-compact"
                  />
                </div>

                <div
                  v-if="studentSearchQuery.length >= 2"
                  class="search-results"
                >
                  <div
                    v-if="filteredStudentOptions.length > 0"
                    class="student-options"
                  >
                    <div
                      v-for="student in filteredStudentOptions"
                      :key="student.id"
                      class="student-option"
                      @click="selectStudent(student)"
                    >
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <strong>{{ student.name }}</strong>
                          <div class="text-muted small">
                            {{ student.className
                            }}{{
                              student.sectionName
                                ? " - " + student.sectionName
                                : ""
                            }}
                            <span v-if="student.grNo" class="ml-2">
                              GR: {{ student.grNo }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="
                      filteredStudentOptions.length === 0 &&
                      !studentSearchLoading
                    "
                    class="empty-state"
                  >
                    <feather-icon icon="SearchIcon" size="32" />
                    <p>No students found</p>
                  </div>

                  <div v-if="studentSearchLoading" class="loading-state">
                    <b-spinner variant="primary" size="sm" />
                    <span>Searching...</span>
                  </div>
                </div>

                <div v-if="form.sID && form.sID.name" class="selected-student">
                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <strong>{{ form.sID.name }}</strong>
                      <div class="text-muted small">
                        {{ form.sID.className
                        }}{{
                          form.sID.sectionName
                            ? " - " + form.sID.sectionName
                            : ""
                        }}
                        <span v-if="form.sID.grNo" class="ml-2">
                          GR: {{ form.sID.grNo }}
                        </span>
                      </div>
                    </div>
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      @click="clearSelectedStudent"
                    >
                      <feather-icon icon="XIcon" size="14" />
                    </b-button>
                  </div>
                </div>
              </b-form-group>
            </b-col>
            <b-col md="6" class="pl-md-1">
              <b-form-group label="Reported By" label-for="reportedBy" required>
                <v-select
                  v-model="form.reportedBy"
                  :options="reportedByOptions"
                  :clearable="false"
                  label="text"
                  :reduce="(val) => val.value"
                  placeholder="Select who reported"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <div class="form-card soft-card">
          <b-row>
            <b-col md="12">
              <b-form-group label="Symptoms" label-for="symptoms" required>
                <b-form-textarea
                  v-model.trim="form.symptoms"
                  placeholder="Describe the symptoms"
                  rows="3"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="12">
              <b-form-group
                label="Action Taken at School"
                label-for="actionTakenAtSchool"
              >
                <b-form-textarea
                  v-model.trim="form.actionTakenAtSchool"
                  placeholder="Describe actions taken at school"
                  rows="3"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="12">
              <b-form-group
                label="Medication Given (if any)"
                label-for="medicationGiven"
              >
                <b-form-textarea
                  v-model.trim="form.medicationGiven"
                  placeholder="List any medications given"
                  rows="2"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="align-items-end">
            <b-col md="6">
              <b-form-group label="Observation" label-for="observation">
                <b-form-textarea
                  v-model.trim="form.observation"
                  placeholder="Enter observation details"
                  rows="2"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                label="Parent Action Required"
                label-for="parentActionRequired"
                required
              >
                <v-select
                  v-model="form.parentActionRequired"
                  :options="parentActionOptions"
                  :clearable="false"
                  label="text"
                  :reduce="(val) => val.value"
                  placeholder="Select option"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="12">
              <b-form-group label="Remarks" label-for="remarks">
                <b-form-textarea
                  v-model.trim="form.remarks"
                  placeholder="Enter remarks and follow-up instructions"
                  rows="3"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </b-form>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false">
          Cancel
        </b-button>
        <b-button variant="primary" @click="saveRecord" :disabled="loading">
          <b-spinner v-if="loading" small />
          {{ isEdit ? "Update" : "Save" }}
        </b-button>
      </template>
    </b-modal>

    <!-- View Modal -->
    <b-modal
      v-model="showViewModal"
      title="Medical Record Details"
      centered
      size="lg"
      body-class="p-3"
      modal-class="view-details-modal"
    >
      <div v-if="selectedRecord" class="view-modal-content">
        <div class="view-info-card">
          <div class="info-left">
            <div class="info-name">
              <feather-icon
                icon="UserIcon"
                size="18"
                class="mr-50 text-primary"
              />
              <div>
                <h5 class="mb-25">
                  {{ (selectedRecord.std && selectedRecord.std.name) || "N/A" }}
                </h5>
                <small class="text-muted d-block">
                  GR:
                  {{
                    (selectedRecord.std && selectedRecord.std.grNo) || "N/A"
                  }}
                  · Class:
                  {{
                    (selectedRecord.std &&
                      selectedRecord.std.cls_current &&
                      selectedRecord.std.cls_current.name) ||
                    "N/A"
                  }}
                </small>
              </div>
            </div>
          </div>
          <div class="info-right">
            <b-badge
              :variant="
                selectedRecord.med &&
                selectedRecord.med.parentActionRequired === 'Yes'
                  ? 'danger'
                  : 'success'
              "
              class="status-chip"
            >
              {{
                (selectedRecord.med &&
                  selectedRecord.med.parentActionRequired) ||
                "N/A"
              }}
            </b-badge>
            <div class="date-time text-right">
              <small class="text-muted d-block"
                >Date:
                {{
                  formatDate(selectedRecord.med && selectedRecord.med.date)
                }}</small
              >
              <small class="text-muted d-block"
                >Reported By:
                {{
                  (selectedRecord.med && selectedRecord.med.reportedBy) || "N/A"
                }}</small
              >
            </div>
          </div>
        </div>

        <div class="view-section">
          <div class="section-header">
            <h6 class="mb-0">Medical Details</h6>
          </div>
          <div class="info-grid">
            <div class="info-card">
              <span class="metric-label">Symptoms</span>
              <p class="info-text">
                {{
                  (selectedRecord.med && selectedRecord.med.symptoms) || "N/A"
                }}
              </p>
            </div>
            <div class="info-card">
              <span class="metric-label">Action Taken at School</span>
              <p class="info-text">
                {{
                  (selectedRecord.med &&
                    selectedRecord.med.actionTakenAtSchool) ||
                  "N/A"
                }}
              </p>
            </div>
            <div class="info-card">
              <span class="metric-label">Medication Given</span>
              <p class="info-text">
                {{
                  (selectedRecord.med && selectedRecord.med.medicationGiven) ||
                  "N/A"
                }}
              </p>
            </div>
            <div class="info-card">
              <span class="metric-label">Observation</span>
              <p class="info-text">
                {{
                  (selectedRecord.med && selectedRecord.med.observation) ||
                  "N/A"
                }}
              </p>
            </div>
            <div class="info-card">
              <span class="metric-label">Remarks</span>
              <p class="info-text">
                {{
                  (selectedRecord.med && selectedRecord.med.remarks) || "N/A"
                }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="showViewModal = false"
        >
          Close
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BButton,
  BTable,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BSpinner,
  BBadge,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { mapActions } from "vuex";
import Swal from "sweetalert2";
import axios from "axios";

export default {
  name: "InHouseDoctor",
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormTextarea,
    BButton,
    BTable,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BSpinner,
    BBadge,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      medicalRecords: [],
      students: [],
      studentOptions: [],
      studentLoading: false,
      classes: [],
      classOptions: [],
      sections: [],
      // Modal-specific data
      modalStudentOptions: [],
      modalSections: [],
      allStudents: [],
      searchQuery: "",
      searchTimeout: null,
      mainSearchTimeout: null,
      autoOpenTimeout: null,
      actionLoadingId: null,
      actionLoadingType: "",
      filters: {
        dateRange: "",
        fromDate: "",
        toDate: "",
      },
      // New search functionality
      studentSearchQuery: "",
      filteredStudentOptions: [],
      studentSearchLoading: false,
      studentSearchTimer: null,
      studentSearchCache: {},
      lastStudentSearchTerm: "",
      lastStudentSearchToken: 0,
      showModal: false,
      showViewModal: false,
      isEdit: false,
      loading: false,
      dataLoading: false,
      selectedRecord: null,
      currentPage: 1,
      perPage: 30,
      totalCount: 0,
      totalPages: 1,
      showPagination: true,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "30", value: 30 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      fields: [
        { key: "sno", label: "S.No", sortable: false },
        { key: "date", label: "Date", sortable: false },
        { key: "studentInfo", label: "Student", sortable: false },
        { key: "reportedBy", label: "Reported By", sortable: false },
        { key: "symptoms", label: "Symptoms", sortable: false },
        { key: "actionTakenAtSchool", label: "Action Taken", sortable: false },
        { key: "medicationGiven", label: "Medication", sortable: false },
        { key: "observation", label: "Observation", sortable: false },
        {
          key: "parentActionRequired",
          label: "Parent Action",
          sortable: false,
        },
        { key: "remarks", label: "Remarks", sortable: false },
        { key: "actions", label: "Actions" },
      ],
      form: {
        id: 0,
        date: this.getCurrentDate(), // Format: yyyy-MM-dd
        time: this.getCurrentTime(), // Format: HH:mm:ss
        sID: null,
        tID: null,
        reportedBy: "",
        symptoms: "",
        actionTakenAtSchool: "",
        medicationGiven: "",
        observation: "",
        parentActionRequired: "",
        remarks: "",
        attachments: "",
        campusID: this.$store.state.userData.cId,
        createdBy: this.$store.state.userData.userID,
        updatedBy: this.$store.state.userData.userID,
      },
      filteredRecords: [],
      referralOptions: [
        { value: "", text: "Select option" },
        { value: "No Action Required", text: "No Action Required" },
        { value: "Observation", text: "Observation" },
        { value: "Refer to Doctor", text: "Refer to Doctor" },
        { value: "Emergency Referral", text: "Emergency Referral" },
        { value: "Parent Contacted", text: "Parent Contacted" },
      ],
      parentActionOptions: [
        { value: "", text: "Select option" },
        { value: "Yes", text: "Yes" },
        { value: "No", text: "No" },
      ],
      reportedByOptions: [
        { value: "Teacher", text: "Teacher" },
        { value: "Parent", text: "Parent" },
        { value: "Student", text: "Student" },
      ],
      dateConfig: {
        dateFormat: "Y-m-d",
        allowInput: true,
        clickOpens: true,
        placeholder: "Select date",
      },
      timeConfig: {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i:S",
        allowInput: true,
        clickOpens: true,
        placeholder: "Select time",
      },
      dateRangeConfig: {
        mode: "range",
        dateFormat: "Y-m-d",
        allowInput: true,
        clickOpens: true,
        placeholder: "Select date range",
      },
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.updateScrollbarWidth();
    });
    window.addEventListener("resize", this.updateScrollbarWidth);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScrollbarWidth);
  },
  computed: {
    hasActiveFilters() {
      return !!(
        this.filters.dateRange ||
        this.filters.fromDate ||
        this.filters.toDate ||
        this.searchQuery
      );
    },
    displayedRange() {
      if (this.totalCount === 0) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    paginatedRecords() {
      return this.filteredRecords;
    },
  },
  created() {
    // Initialize rights from menu and enforce view permission
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
          assignSections: ch.assignSections,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    this.loadMedicalRecords();
    this.loadStudents();
    this.loadClasses();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    updateScrollbarWidth() {
      this.$nextTick(() => {
        const tableWrapper = this.$refs.tableWrapper;
        const topScrollbar = this.$refs.topScrollbar;
        const topContent =
          topScrollbar &&
          topScrollbar.querySelector(".table-scrollbar-content");
        if (tableWrapper && topContent) {
          topContent.style.width = `${tableWrapper.scrollWidth}px`;
          topScrollbar.scrollLeft = tableWrapper.scrollLeft;
        }
      });
    },

    syncScroll(event) {
      const tableWrapper = this.$refs.tableWrapper;
      const topScrollbar = this.$refs.topScrollbar;
      if (!tableWrapper || !topScrollbar) return;
      if (event && event.target === tableWrapper) {
        topScrollbar.scrollLeft = tableWrapper.scrollLeft;
      } else if (event && event.target === topScrollbar) {
        tableWrapper.scrollLeft = topScrollbar.scrollLeft;
      }
    },

    getCurrentTime() {
      const now = new Date();
      return now.toTimeString().split(" ")[0]; // Format: HH:mm:ss
    },

    getCurrentDate() {
      const now = new Date();
      return now.toISOString().split("T")[0]; // Format: yyyy-MM-dd
    },

    async loadStudents() {
      this.studentLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Students/LoadDataPagination?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&clsID=0&section=0&page=1&status=present",
          token: this.$store.state.userData.token,
        };
        console.log("Loading students with URL:", obj.url);
        const response = await this.get(obj);
        console.log("Main students response:", response);

        // Handle different response structures
        let studentsData = [];
        if (response && response.data && Array.isArray(response.data.data)) {
          studentsData = response.data.data;
          console.log("Using response.data.data structure");
        } else if (response && Array.isArray(response.data)) {
          studentsData = response.data;
          console.log("Using response.data structure");
        } else if (Array.isArray(response)) {
          studentsData = response;
          console.log("Using direct response structure");
        } else {
          console.log("No valid data structure found in response");
        }

        console.log("Extracted students data:", studentsData);

        this.students = studentsData.map((student) => ({
          id: student.id,
          name: student.name || "Unknown Student",
        }));
        this.studentOptions = studentsData.map((student) => {
          // Parse meta_class field to extract class and section
          let className = "No Class";
          let sectionName = "";

          if (student.meta_class) {
            // Split by " - " to separate class and section
            const parts = student.meta_class.split(" - ");
            if (parts.length >= 2) {
              className = parts[0].trim(); // e.g., "KG-2"
              sectionName = parts[1].trim(); // e.g., "Tulips"
            } else if (parts.length === 1) {
              className = parts[0].trim();
            }
          }

          return {
            ...student,
            name: student.name || "Unknown Student",
            className: className,
            sectionName: sectionName,
          };
        });

        console.log("Final student options:", this.studentOptions);
        console.log("Student options length:", this.studentOptions.length);
      } catch (error) {
        console.error("Main student load error:", error);
        this.students = [];
        this.studentOptions = [];
      } finally {
        this.studentLoading = false;
      }
    },

    async loadClasses() {
      const obj = {
        url:
          this.$store.state.domain +
          "myclasses/LoadClasses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId +
          "&dID=0",
        token: this.$store.state.userData.token,
      };
      const classesData = await this.get(obj);
      this.classes = classesData;
      this.classOptions = this.classes.map((el) => el.cls);
    },

    onClassChange() {
      this.filters.sectionID = null;
      this.filters.studentID = null;
      this.sections = [];

      if (this.filters.classID) {
        const selectedClass = this.classes.find(
          (el) => el.cls.id === this.filters.classID
        );
        if (
          selectedClass &&
          selectedClass.sections &&
          selectedClass.sections.length > 0
        ) {
          this.sections = selectedClass.sections;
        }
        // Load students for the selected class (all sections)
        this.loadStudentsByClass();
      } else {
        // If no class selected, load all students
        this.loadStudents();
      }
      this.onFilterChange();
    },

    onSectionChange() {
      this.filters.studentID = null;

      if (this.filters.sectionID) {
        this.loadStudentsBySection();
      } else if (this.filters.classID) {
        // If section is cleared but class is still selected, load all students from that class
        this.loadStudentsByClass();
      } else {
        // If no class or section selected, load all students
        this.loadStudents();
      }
      this.onFilterChange();
    },

    async loadStudentsByClass() {
      if (!this.filters.classID) return;

      this.studentLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}students/Filterdata?cls=${this.filters.classID}&sec=0&status=present&db=${this.$store.state.userData.db}&cId=${this.$store.state.userData.cId}&page=1`,
          token: this.$store.state.userData.token,
        };
        console.log("Loading students by class with URL:", obj.url);
        const response = await this.get(obj);
        console.log("Students by class response:", response);

        // Handle different response structures
        let studentsData = [];
        if (response && response.data && Array.isArray(response.data.data)) {
          studentsData = response.data.data;
          console.log("Using response.data.data structure");
        } else if (response && Array.isArray(response.data)) {
          studentsData = response.data;
          console.log("Using response.data structure");
        } else if (Array.isArray(response)) {
          studentsData = response;
          console.log("Using direct response structure");
        } else {
          console.log("No valid data structure found in response");
        }

        console.log("Extracted students data by class:", studentsData);

        this.studentOptions = studentsData.map((student) => {
          // Parse meta_class field to extract class and section
          let className = "No Class";
          let sectionName = "";

          if (student.meta_class) {
            // Split by " - " to separate class and section
            const parts = student.meta_class.split(" - ");
            if (parts.length >= 2) {
              className = parts[0].trim(); // e.g., "KG-2"
              sectionName = parts[1].trim(); // e.g., "Tulips"
            } else if (parts.length === 1) {
              className = parts[0].trim();
            }
          }

          return {
            ...student,
            name: student.name || "Unknown Student",
            className: className,
            sectionName: sectionName,
          };
        });

        console.log("Final student options by class:", this.studentOptions);
        console.log(
          "Student options length by class:",
          this.studentOptions.length
        );
      } catch (error) {
        console.error("Student load error by class:", error);
        this.studentOptions = [];
      } finally {
        this.studentLoading = false;
      }
    },

    async loadStudentsBySection() {
      if (!this.filters.classID || !this.filters.sectionID) return;

      this.studentLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}students/Filterdata?cls=${this.filters.classID}&sec=${this.filters.sectionID}&status=present&db=${this.$store.state.userData.db}&cId=${this.$store.state.userData.cId}&page=1`,
          token: this.$store.state.userData.token,
        };
        console.log("Loading students by section with URL:", obj.url);
        const response = await this.get(obj);
        console.log("Students by section response:", response);

        // Handle different response structures
        let studentsData = [];
        if (response && response.data && Array.isArray(response.data.data)) {
          studentsData = response.data.data;
          console.log("Using response.data.data structure");
        } else if (response && Array.isArray(response.data)) {
          studentsData = response.data;
          console.log("Using response.data structure");
        } else if (Array.isArray(response)) {
          studentsData = response;
          console.log("Using direct response structure");
        } else {
          console.log("No valid data structure found in response");
        }

        console.log("Extracted students data by section:", studentsData);

        this.studentOptions = studentsData.map((student) => {
          // Parse meta_class field to extract class and section
          let className = "No Class";
          let sectionName = "";

          if (student.meta_class) {
            // Split by " - " to separate class and section
            const parts = student.meta_class.split(" - ");
            if (parts.length >= 2) {
              className = parts[0].trim(); // e.g., "KG-2"
              sectionName = parts[1].trim(); // e.g., "Tulips"
            } else if (parts.length === 1) {
              className = parts[0].trim();
            }
          }

          return {
            ...student,
            name: student.name || "Unknown Student",
            className: className,
            sectionName: sectionName,
          };
        });

        console.log("Final student options by section:", this.studentOptions);
        console.log(
          "Student options length by section:",
          this.studentOptions.length
        );
      } catch (error) {
        console.error("Student load error by section:", error);
        this.studentOptions = [];
      } finally {
        this.studentLoading = false;
      }
    },

    async searchStudents(search) {
      if (search.length < 2) return;

      this.studentLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}Students/Search?db=${
            this.$store.state.userData.db
          }&cID=${this.$store.state.userData.cId}&keyword=${encodeURIComponent(
            search
          )}&type=`,
          token: this.$store.state.userData.token,
        };
        console.log("Searching students with URL:", obj.url);
        const response = await this.get(obj);
        console.log("Students search response:", response);

        // Handle different response structures
        let studentsData = [];
        if (response && response.data && Array.isArray(response.data.data)) {
          studentsData = response.data.data;
          console.log("Using response.data.data structure");
        } else if (response && Array.isArray(response.data)) {
          studentsData = response.data;
          console.log("Using response.data structure");
        } else if (Array.isArray(response)) {
          studentsData = response;
          console.log("Using direct response structure");
        } else {
          console.log("No valid data structure found in response");
        }

        console.log("Extracted students data by search:", studentsData);

        this.studentOptions = studentsData.map((student) => {
          // Parse meta_class field to extract class and section
          let className = "No Class";
          let sectionName = "";

          if (student.meta_class) {
            // Split by " - " to separate class and section
            const parts = student.meta_class.split(" - ");
            if (parts.length >= 2) {
              className = parts[0].trim(); // e.g., "KG-2"
              sectionName = parts[1].trim(); // e.g., "Tulips"
            } else if (parts.length === 1) {
              className = parts[0].trim();
            }
          }

          return {
            ...student,
            name: student.name || "Unknown Student",
            className: className,
            sectionName: sectionName,
          };
        });

        console.log("Final student options by search:", this.studentOptions);
        console.log(
          "Student options length by search:",
          this.studentOptions.length
        );
      } catch (error) {
        console.error("Student search error:", error);
        this.studentOptions = [];
      } finally {
        this.studentLoading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    buildMedicalQuery(page = this.currentPage, pageSize = this.perPage) {
      const params = new URLSearchParams({
        db: this.$store.state.userData.db,
        cID: this.$store.state.userData.cId,
        page,
        pageSize,
      });
      if (this.searchQuery && this.searchQuery.trim()) {
        params.append("search", this.searchQuery.trim());
      }
      if (this.filters.fromDate)
        params.append("fromDate", this.filters.fromDate);
      if (this.filters.toDate) params.append("toDate", this.filters.toDate);
      return params.toString();
    },

    async loadMedicalRecords() {
      this.dataLoading = true;
      try {
        const query = this.buildMedicalQuery(this.currentPage, this.perPage);
        const obj = {
          url: `${this.$store.state.domain}MedicalVisits/loadData?${query}`,
          token: this.$store.state.userData.token,
        };
        const response = await axios.get(obj.url, {
          headers: { Authorization: `Bearer ${obj.token}` },
        });
        const payload = response?.data ?? response ?? {};
        const container = payload?.data;
        let records = [];
        if (Array.isArray(container?.data)) {
          records = container.data;
        } else if (Array.isArray(container)) {
          records = container;
        } else if (Array.isArray(payload)) {
          records = payload;
        }

        const meta = {
          totalCount:
            payload?.totalCount ??
            container?.totalCount ??
            payload?.data?.totalCount ??
            payload?.totalRecords ??
            container?.totalRecords ??
            payload?.data?.totalRecords ??
            null,
          page: payload?.page ?? container?.page ?? payload?.data?.page ?? null,
          pageSize:
            payload?.pageSize ??
            container?.pageSize ??
            payload?.data?.pageSize ??
            null,
          totalPages:
            payload?.totalPages ??
            container?.totalPages ??
            payload?.data?.totalPages ??
            null,
        };

        const sortedData = records.sort((a, b) => {
          const dateA = new Date(a.med && a.med.date ? a.med.date : 0);
          const dateB = new Date(b.med && b.med.date ? b.med.date : 0);
          return dateB - dateA;
        });

        const pageSizeFromApi = Number(meta.pageSize) || this.perPage;
        const totalRecords =
          Number(meta.totalCount) ||
          (meta.totalPages && pageSizeFromApi
            ? Number(meta.totalPages) * pageSizeFromApi
            : null) ||
          sortedData.length;
        const totalPages =
          Number(meta.totalPages) ||
          Math.ceil(
            (totalRecords || sortedData.length) /
              (pageSizeFromApi || this.perPage)
          );
        const pageFromApi = Number(meta.page) || this.currentPage || 1;

        this.medicalRecords = sortedData;
        this.filteredRecords = sortedData;
        this.totalCount = totalRecords;
        this.perPage = pageSizeFromApi || this.perPage;
        this.currentPage = pageFromApi || this.currentPage || 1;
        this.totalPages = totalPages;
        if (this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages || 1;
        }
      } catch (error) {
        console.error("Error loading medical records:", error);
        this.medicalRecords = [];
        this.filteredRecords = [];
        this.totalCount = 0;
      } finally {
        this.dataLoading = false;
      }
    },

    onSearchChange() {
      // Clear previous timeout
      if (this.mainSearchTimeout) {
        clearTimeout(this.mainSearchTimeout);
      }

      // Debounce the search to avoid filtering on every keystroke
      this.mainSearchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadMedicalRecords();
      }, 500); // 500ms debounce delay
    },

    onDateRangeChange(selectedDates) {
      if (selectedDates && selectedDates.length === 2) {
        this.filters.fromDate = selectedDates[0];
        this.filters.toDate = selectedDates[1];
      } else {
        this.filters.fromDate = "";
        this.filters.toDate = "";
      }
      this.currentPage = 1;
      this.loadMedicalRecords();
    },

    clearFilters() {
      this.filters = {
        dateRange: "",
        fromDate: "",
        toDate: "",
      };
      this.searchQuery = "";
      this.currentPage = 1;
      this.loadMedicalRecords();
    },

    filterRecords() {
      if (!this.searchQuery.trim()) {
        this.filteredRecords = this.medicalRecords;
      } else {
        const query = this.searchQuery.toLowerCase();
        const filtered = this.medicalRecords.filter((item) => {
          const studentName = (item.std && item.std.name) || "";
          const symptoms = (item.med && item.med.symptoms) || "";
          const medication = (item.med && item.med.medicationGiven) || "";
          const remarks = (item.med && item.med.remarks) || "";
          const actionTaken = (item.med && item.med.actionTakenAtSchool) || "";
          const observation = (item.med && item.med.observation) || "";
          const reportedBy = (item.med && item.med.reportedBy) || "";

          return (
            studentName.toLowerCase().includes(query) ||
            symptoms.toLowerCase().includes(query) ||
            medication.toLowerCase().includes(query) ||
            remarks.toLowerCase().includes(query) ||
            actionTaken.toLowerCase().includes(query) ||
            observation.toLowerCase().includes(query) ||
            reportedBy.toLowerCase().includes(query)
          );
        });

        // Maintain sorting by date (latest first) after filtering
        this.filteredRecords = filtered.sort((a, b) => {
          const dateA = new Date(a.med && a.med.date ? a.med.date : 0);
          const dateB = new Date(b.med && b.med.date ? b.med.date : 0);
          return dateB - dateA; // Descending order (latest first)
        });
      }
      this.totalCount = this.filteredRecords.length;
      const pages = Math.max(
        1,
        Math.ceil((this.totalCount || 0) / this.perPage || 1)
      );
      if (this.currentPage > pages) {
        this.currentPage = 1;
      }
    },

    getActionId(item) {
      return item?.id || (item.med && item.med.id) || null;
    },

    isActionLoading(item, type) {
      return (
        this.actionLoadingId === this.getActionId(item) &&
        this.actionLoadingType === type
      );
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadMedicalRecords();
    },
    closePageSizeDropdown() {
      if (
        this.$refs.pageSizeSelect &&
        this.$refs.pageSizeSelect.toggleDropdown
      ) {
        this.$refs.pageSizeSelect.toggleDropdown(false);
      }
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadMedicalRecords();
    },

    openAddModal() {
      this.isEdit = false;
      this.resetModalFilters();
      this.loadModalStudents();
      // Update time to current time when opening modal
      this.form.time = this.getCurrentTime();
      this.form.date = this.getCurrentDate();
      this.showModal = true;
    },

    viewRecord(item) {
      this.actionLoadingId = this.getActionId(item);
      this.actionLoadingType = "view";
      try {
        this.selectedRecord = item;
        this.showViewModal = true;
      } finally {
        this.actionLoadingId = null;
        this.actionLoadingType = "";
      }
    },

    async editRecord(item) {
      this.actionLoadingId = this.getActionId(item);
      this.actionLoadingType = "edit";
      this.isEdit = true;

      // Parse the date and time from the existing date field
      const existingDate =
        (item.med && item.med.date) || new Date().toISOString();
      const dateObj = new Date(existingDate);
      const dateString = dateObj.toISOString().split("T")[0]; // Format: yyyy-MM-dd
      const timeString = dateObj.toTimeString().split(" ")[0]; // Format: HH:mm:ss

      // Map the nested structure to form fields
      this.form = {
        id: (item.med && item.med.id) || 0,
        date: dateString,
        time: timeString,
        sID: (item.med && item.med.sID) || null,
        tID: (item.med && item.med.tID) || 0,
        reportedBy: (item.med && item.med.reportedBy) || "",
        symptoms: (item.med && item.med.symptoms) || "",
        actionTakenAtSchool: (item.med && item.med.actionTakenAtSchool) || "",
        medicationGiven: (item.med && item.med.medicationGiven) || "",
        observation: (item.med && item.med.observation) || "",
        parentActionRequired: (item.med && item.med.parentActionRequired) || "",
        remarks: (item.med && item.med.remarks) || "",
        attachments: (item.med && item.med.attachments) || "",
        campusID:
          (item.med && item.med.campusID) || this.$store.state.userData.cId,
        createdBy:
          (item.med && item.med.createdBy) || this.$store.state.userData.userID,
        updatedBy:
          (item.med && item.med.updatedBy) || this.$store.state.userData.userID,
        createdOn: (item.med && item.med.createdOn) || new Date().toISOString(),
        updatedOn: (item.med && item.med.updatedOn) || null,
      };

      // Get student name from the item data and create the student object for display
      const studentName = (item.std && item.std.name) || "Unknown Student";

      // Parse meta_class field to extract class and section
      let className = "No Class";
      let sectionName = "";
      let grNo = "";

      if (item.std && item.std.meta_class) {
        // Split by " - " to separate class and section
        const parts = item.std.meta_class.split(" - ");
        if (parts.length >= 2) {
          className = parts[0].trim(); // e.g., "KG-2"
          sectionName = parts[1].trim(); // e.g., "Tulips"
        } else if (parts.length === 1) {
          className = parts[0].trim();
        }
      }

      // Get GR number
      grNo = (item.std && item.std.grNo) || "";

      // Create the student object for v-select display
      this.form.sID = {
        id: this.form.sID,
        name: studentName,
        className: className,
        sectionName: sectionName,
        grNo: grNo,
        displayName: `${studentName} - ${className}${
          sectionName ? " - " + sectionName : ""
        }`,
      };

      // Set the student in modal options so it shows in the dropdown
      this.modalStudentOptions = [this.form.sID];

      this.showModal = true;
      this.actionLoadingId = null;
      this.actionLoadingType = "";
    },

    async deleteRecord(item) {
      if (!this.rights?.delete) {
        this.$bvToast.toast("You don't have permission to delete.", {
          title: "Not Allowed",
          variant: "danger",
          solid: true,
        });
        return;
      }

      const recordId = this.getActionId(item);
      if (!recordId) {
        this.$bvToast.toast("Unable to delete: missing record id.", {
          title: "Delete Failed",
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.actionLoadingId = recordId;
      this.actionLoadingType = "delete";
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        const obj = {
          url:
            this.$store.state.domain +
            "MedicalVisits/" +
            recordId +
            "?db=" +
            this.$store.state.userData.db,
          message: "Medical record deleted successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };
        const success = await this.delete(obj);
        if (success) {
          this.loadMedicalRecords();
        }
      }
      this.actionLoadingId = null;
      this.actionLoadingType = "";
    },

    async saveRecord() {
      if (
        !this.form.sID ||
        !this.form.reportedBy ||
        !this.form.symptoms ||
        !this.form.parentActionRequired ||
        !this.form.date ||
        !this.form.time
      ) {
        this.$bvToast.toast("Please fill all required fields!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.loading = true;

      // Prepare form data for save/edit
      const formData = { ...this.form };

      // Combine date and time into proper ISO format
      const combinedDateTime = `${formData.date}T${formData.time}`;
      formData.date = combinedDateTime;

      // Remove the separate time field as it's now combined with date
      delete formData.time;

      // Handle student ID - if sID is an object, extract the id
      if (formData.sID && typeof formData.sID === "object") {
        formData.sID = formData.sID.id;
      }

      // Always pass tID as 0
      formData.tID = 0;

      // Handle createdOn and updatedOn based on operation type
      if (this.isEdit) {
        // For edit operations
        formData.updatedBy = this.$store.state.userData.userID;
        formData.updatedOn = new Date().toISOString();
        // Keep existing createdOn, don't modify it
      } else {
        // For new records
        formData.createdOn = new Date().toISOString();
        formData.updatedOn = null;
        // Remove updatedBy for new records
        delete formData.updatedBy;
      }

      const obj = {
        url:
          this.$store.state.domain +
          "MedicalVisits" +
          (this.isEdit ? "/" + formData.id : "") +
          "?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: formData,
        message:
          "Medical record " +
          (this.isEdit ? "updated" : "created") +
          " successfully.",
        context: this,
        token: this.$store.state.userData.token,
      };

      const success = this.isEdit ? await this.put(obj) : await this.post(obj);

      if (success) {
        this.showModal = false;
        this.loadMedicalRecords();
      }
      this.loading = false;
    },

    resetForm() {
      this.form = {
        id: 0,
        date: this.getCurrentDate(), // Format: yyyy-MM-dd
        time: this.getCurrentTime(), // Format: HH:mm:ss
        sID: null,
        tID: null,
        reportedBy: "",
        symptoms: "",
        actionTakenAtSchool: "",
        medicationGiven: "",
        observation: "",
        parentActionRequired: "",
        remarks: "",
        attachments: "",
        campusID: this.$store.state.userData.cId,
        createdBy: this.$store.state.userData.userID,
        // updatedBy will be set only during edit operations
      };
      this.resetModalFilters();
    },

    resetModalFilters() {
      this.modalStudentOptions = [];
      this.allStudents = [];
    },

    async loadModalStudents() {
      // Load some default students to show the dropdown is working
      try {
        const obj = {
          url: `${this.$store.state.domain}Students/LoadDataPagination?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&clsID=0&section=0&page=1&status=present`,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);

        let studentsData = [];
        if (response && response.data && Array.isArray(response.data.data)) {
          studentsData = response.data.data.slice(0, 15); // Get first 15 students as default
        } else if (response && Array.isArray(response.data)) {
          studentsData = response.data.slice(0, 15);
        } else if (Array.isArray(response)) {
          studentsData = response.slice(0, 15);
        }

        this.modalStudentOptions = studentsData.map((student) => {
          // Parse meta_class field to extract class and section
          let className = "No Class";
          let sectionName = "";

          if (student.meta_class) {
            // Split by " - " to separate class and section
            const parts = student.meta_class.split(" - ");
            if (parts.length >= 2) {
              className = parts[0].trim(); // e.g., "KG-2"
              sectionName = parts[1].trim(); // e.g., "Tulips"
            } else if (parts.length === 1) {
              className = parts[0].trim();
            }
          }

          return {
            ...student,
            displayName: student.name || "Unknown Student",
            name: student.name || "Unknown Student",
            className: className,
            sectionName: sectionName,
          };
        });

        console.log(
          "Default student options loaded:",
          this.modalStudentOptions.length
        );
      } catch (error) {
        console.error("Error loading default students:", error);
        this.modalStudentOptions = [];
      }
    },

    onStudentDropdownOpen() {
      // When dropdown opens, only clear if no search has been performed
      if (this.modalStudentOptions.length === 0) {
        // No search performed yet, keep it empty
        return;
      }
      // If search was performed and results exist, keep them visible
    },

    onStudentSelectionChange(selectedStudent) {
      // If student is cleared (null), reload default students
      if (!selectedStudent) {
        this.loadModalStudents();
      }
    },

    // New search functionality methods
    async onStudentSearchChange() {
      // Debounce to reduce API hits
      if (this.studentSearchTimer) clearTimeout(this.studentSearchTimer);
      this.studentSearchTimer = setTimeout(() => {
        this.performStudentSearch();
      }, 700);
    },

    async performStudentSearch() {
      const term = (this.studentSearchQuery || "").trim().toLowerCase();
      if (term.length < 2) {
        this.filteredStudentOptions = [];
        this.lastStudentSearchTerm = "";
        return;
      }

      // If cached, return without API call
      if (this.studentSearchCache[term]) {
        this.filteredStudentOptions = this.studentSearchCache[term];
        this.lastStudentSearchTerm = term;
        return;
      }

      const token = ++this.lastStudentSearchToken;
      this.studentSearchLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}Students/Search?db=${
            this.$store.state.userData.db
          }&cID=${this.$store.state.userData.cId}&keyword=${encodeURIComponent(
            this.studentSearchQuery
          )}&type=`,
          token: this.$store.state.userData.token,
        };

        console.log("Searching students with URL:", obj.url);
        const response = await this.get(obj);
        if (token !== this.lastStudentSearchToken) return;
        console.log("Student search response:", response);

        // Handle different response structures
        let studentsData = [];
        if (response && response.data && Array.isArray(response.data)) {
          studentsData = response.data;
        } else if (Array.isArray(response)) {
          studentsData = response;
        }

        // Map students with proper structure for display
        const mapped = studentsData.map((student) => {
          // Parse meta_class field to extract class and section
          let className = "No Class";
          let sectionName = "";

          if (student.meta_class) {
            // Split by " - " to separate class and section
            const parts = student.meta_class.split(" - ");
            if (parts.length >= 2) {
              className = parts[0].trim(); // e.g., "KG-2"
              sectionName = parts[1].trim(); // e.g., "Tulips"
            } else if (parts.length === 1) {
              className = parts[0].trim();
            }
          }

          return {
            ...student,
            name: student.name || "Unknown Student",
            className: className,
            sectionName: sectionName,
            grNo: student.grNo || "",
            sections: student.sections || null,
          };
        });

        this.filteredStudentOptions = mapped;
        this.studentSearchCache[term] = mapped;
        this.lastStudentSearchTerm = term;

        console.log(
          "Filtered student options:",
          this.filteredStudentOptions.length,
          "students"
        );
      } catch (error) {
        console.error("Student search error:", error);
        this.filteredStudentOptions = [];
      } finally {
        if (token === this.lastStudentSearchToken) {
          this.studentSearchLoading = false;
        }
      }
    },

    selectStudent(student) {
      this.form.sID = student;
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
    },

    clearSelectedStudent() {
      this.form.sID = null;
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
    },

    async searchModalStudents(search) {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // If search is empty, don't show any students
      if (!search || search.trim() === "") {
        this.modalStudentOptions = [];
        return;
      }

      // Debounce search to avoid too many API calls (increased delay for slow typers)
      this.searchTimeout = setTimeout(async () => {
        this.studentLoading = true;
        try {
          const obj = {
            url: `${this.$store.state.domain}Students/Search?db=${
              this.$store.state.userData.db
            }&cID=${
              this.$store.state.userData.cId
            }&keyword=${encodeURIComponent(search)}&type=`,
            token: this.$store.state.userData.token,
          };
          console.log("Searching students with URL:", obj.url);
          const response = await this.get(obj);
          console.log("Students search response:", response);

          // Handle the response structure from Students/Search
          let studentsData = [];
          if (response && response.data && Array.isArray(response.data)) {
            studentsData = response.data;
          } else if (Array.isArray(response)) {
            studentsData = response;
          }

          // Map students with proper structure for v-select
          this.modalStudentOptions = studentsData.map((student) => {
            // Parse meta_class field to extract class and section
            let className = "No Class";
            let sectionName = "";

            if (student.meta_class) {
              // Split by " - " to separate class and section
              const parts = student.meta_class.split(" - ");
              if (parts.length >= 2) {
                className = parts[0].trim(); // e.g., "KG-2"
                sectionName = parts[1].trim(); // e.g., "Tulips"
              } else if (parts.length === 1) {
                className = parts[0].trim();
              }
            }

            return {
              ...student,
              displayName: student.name || "Unknown Student",
              name: student.name || "Unknown Student",
              className: className,
              sectionName: sectionName,
            };
          });

          console.log("Filtered student options:", this.modalStudentOptions);
          console.log(
            "Modal student options length:",
            this.modalStudentOptions.length
          );

          // Auto-open dropdown immediately when search results are available
          if (this.modalStudentOptions.length > 0) {
            // Add a small delay to ensure DOM is ready
            setTimeout(() => {
              this.$nextTick(() => {
                if (this.$refs.studentSelect) {
                  // Try multiple methods to open the dropdown
                  try {
                    // Method 1: Try setting open property
                    if (this.$refs.studentSelect.open !== undefined) {
                      this.$refs.studentSelect.open = true;
                    }
                    // Method 2: Try calling the open method if it exists
                    else if (
                      typeof this.$refs.studentSelect.open === "function"
                    ) {
                      this.$refs.studentSelect.open();
                    }
                    // Method 3: Trigger click on dropdown toggle
                    else {
                      const dropdownToggle =
                        this.$refs.studentSelect.$el?.querySelector(
                          ".vs__dropdown-toggle"
                        );
                      if (dropdownToggle) {
                        dropdownToggle.click();
                      }
                    }
                  } catch (error) {
                    console.log("Auto-open dropdown error:", error);
                    // Fallback: try direct DOM manipulation
                    const dropdownToggle =
                      this.$refs.studentSelect.$el?.querySelector(
                        ".vs__dropdown-toggle"
                      );
                    if (dropdownToggle) {
                      dropdownToggle.click();
                    }
                  }
                }
              });
            }, 100); // 100ms delay to ensure DOM is ready
          }
        } catch (error) {
          console.error("Student search error:", error);
          this.modalStudentOptions = [];
        } finally {
          this.studentLoading = false;
        }
      }, 800); // 800ms debounce for slow typers
    },

    // Print methods
    async openPrintModal() {
      try {
        const allData = await this.fetchAllRecordsForPrint();
        if (allData && Array.isArray(allData) && allData.length > 0) {
          this.filteredRecords = allData;
        }
      } catch (err) {
        console.error("Error fetching all data for print:", err);
      }
      this.printMedicalReport();
    },

    async loadAllMedicalRecordsForPrint() {
      try {
        const obj = {
          url: `${this.$store.state.domain}MedicalVisits/loadData?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}`,
          token: this.$store.state.userData.token,
        };

        console.log("Loading medical records for print with URL:", obj.url);

        const response = await this.get(obj);

        console.log("Print API Response:", response);

        if (response && response.data && Array.isArray(response.data)) {
          this.medicalRecords = response.data || [];
          this.filteredRecords = response.data || [];
          console.log(
            "All medical records loaded for print:",
            this.medicalRecords.length,
            "records"
          );
          console.log("Sample record:", this.medicalRecords[0]);
        } else {
          this.medicalRecords = [];
          this.filteredRecords = [];
          console.log("No medical records data received for print");
        }
      } catch (error) {
        console.error("Error loading all medical records for print:", error);
        this.medicalRecords = [];
        this.filteredRecords = [];
      }
    },

    async fetchAllRecordsForPrint() {
      try {
        const desiredPageSize =
          this.totalCount > 0
            ? this.totalCount
            : this.totalPages && this.perPage
            ? this.totalPages * this.perPage
            : Math.max(this.perPage, 500);
        const query = this.buildMedicalQuery(1, desiredPageSize);
        const obj = {
          url: `${this.$store.state.domain}MedicalVisits/loadData?${query}`,
          token: this.$store.state.userData.token,
        };
        const response = await axios.get(obj.url, {
          headers: { Authorization: `Bearer ${obj.token}` },
        });
        const payload = response?.data ?? response ?? {};
        const container = payload?.data;
        let records = [];
        if (Array.isArray(container?.data)) {
          records = container.data;
        } else if (Array.isArray(container)) {
          records = container;
        } else if (Array.isArray(payload)) {
          records = payload;
        }
        // Sort latest first
        const sortedData = records.sort((a, b) => {
          const dateA = new Date(a.med && a.med.date ? a.med.date : 0);
          const dateB = new Date(b.med && b.med.date ? b.med.date : 0);
          return dateB - dateA;
        });
        return sortedData;
      } catch (error) {
        console.error("Error fetching all medical records for print:", error);
        return this.filteredRecords || [];
      }
    },

    printMedicalReport() {
      console.log("Generating medical records print report...");

      // Create the HTML content for the print view
      const printContent = this.generatePrintableHTML();

      // Open new window/tab
      const newWindow = window.open(
        "",
        "_blank",
        "width=1200,height=800,scrollbars=yes,resizable=yes"
      );

      if (newWindow) {
        newWindow.document.write(printContent);
        newWindow.document.close();

        // Add print functionality after the content is loaded
        newWindow.onload = function () {
          // Add print button functionality
          const printBtn = newWindow.document.getElementById("printButton");
          if (printBtn) {
            printBtn.addEventListener("click", function () {
              newWindow.print();
            });
          }
        };
      } else {
        alert(
          "Pop-up blocked! Please allow pop-ups for this site to view the medical records report."
        );
      }
    },

    generatePrintableHTML() {
      // Use current table data (filteredRecords) for printing
      const dataToUse = this.filteredRecords || [];

      // Generate table rows with pagination (max 50 records per page)
      const totalRecords = dataToUse.length;
      console.log("Generating print HTML with records:", totalRecords);
      console.log("Data to use:", dataToUse);

      const recordsPerPage = 50;
      const totalPages = Math.ceil(totalRecords / recordsPerPage);

      let allTableRows = "";

      for (let page = 0; page < totalPages; page++) {
        const startIndex = page * recordsPerPage;
        const endIndex = Math.min(startIndex + recordsPerPage, totalRecords);
        const pageRecords = dataToUse.slice(startIndex, endIndex);

        console.log("Page records for page", page, ":", pageRecords);

        const tableRows = pageRecords
          .map((item, index) => {
            console.log("Processing item", index, ":", item);
            const date = this.formatDate(item.med && item.med.date);
            const studentName = (item.std && item.std.name) || "N/A";
            const className =
              (item.std && item.std.cls_current && item.std.cls_current.name) ||
              "N/A";
            const reportedBy = (item.med && item.med.reportedBy) || "N/A";
            const symptoms = (item.med && item.med.symptoms) || "N/A";
            const actionTaken =
              (item.med && item.med.actionTakenAtSchool) || "N/A";
            const medication = (item.med && item.med.medicationGiven) || "N/A";
            const observation = (item.med && item.med.observation) || "N/A";
            const parentAction =
              (item.med && item.med.parentActionRequired) || "N/A";
            const remarks = (item.med && item.med.remarks) || "N/A";

            return `
        <tr ${index % 2 === 0 ? 'class="even-row"' : ""}>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${
            startIndex + index + 1
          }</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${date}</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">
            <div style="font-weight: bold;">${studentName}</div>
            <div style="font-size: 10px; color: #666;">${className}</div>
          </td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${reportedBy}</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${symptoms}</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${actionTaken}</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${medication}</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${observation}</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">
            <span class="badge ${
              parentAction === "Yes" ? "badge-danger" : "badge-success"
            }">${parentAction}</span>
          </td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${remarks}</td>
        </tr>
      `;
          })
          .join("");

        // Add page break for multiple pages
        const pageBreak =
          page < totalPages - 1 ? "page-break-after: always;" : "";

        allTableRows += `
          <div style="${pageBreak}">
            ${
              page > 0
                ? `<div class="page-header"><h2>Medical Records Report - Page ${
                    page + 1
                  } of ${totalPages}</h2></div>`
                : ""
            }
            <table class="report-table" style="border: 1px solid #ccc !important; border-collapse: collapse !important; border-spacing: 0 !important;">
              ${
                page === 0
                  ? `
              <thead>
                <tr>
                  <th class="text-center" style="width: 5%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">S.NO</th>
                  <th class="text-left" style="width: 12%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Date</th>
                  <th class="text-left" style="width: 15%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Student</th>
                  <th class="text-left" style="width: 10%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Reported By</th>
                  <th class="text-left" style="width: 15%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Symptoms</th>
                  <th class="text-left" style="width: 15%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Action Taken</th>
                  <th class="text-left" style="width: 10%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Medication</th>
                  <th class="text-left" style="width: 10%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Observation</th>
                  <th class="text-center" style="width: 8%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Parent Action</th>
                  <th class="text-left" style="width: 6%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Remarks</th>
                </tr>
              </thead>
              `
                  : ""
              }
              <tbody>
                ${tableRows}
              </tbody>
            </table>
          </div>
        `;
      }

      return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Medical Records Report</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Arial', sans-serif;
              line-height: 1.4;
              color: #333;
              background-color: #fff !important;
              padding: 15px;
              font-size: 12px;
              margin: 0;
              width: 100%;
            }
            
            .print-header {
              text-align: center;
              margin-bottom: 10px;
              padding: 8px;
              border-radius: 6px;
              color: #212529;
              background: #f8f9fa;
            }
            
            .print-header h1 {
              margin: 0 0 5px 0;
              color: #212529;
              font-size: 20px;
            }
            
            .print-header p {
              font-size: 14px;
              opacity: 0.9;
              margin: 3px 0;
            }
            
            
            .page-header {
              text-align: center;
              margin-bottom: 20px;
              padding: 10px;
              background: #f8f9fa;
              border-radius: 6px;
              border: 1px solid #e9ecef;
            }
            
            .page-header h2 {
              margin: 0;
              color: #667eea;
              font-size: 18px;
              font-weight: bold;
            }
            
            .report-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 10px;
              background: white;
              border: 1px solid #ccc !important;
              border-spacing: 0 !important;
            }
            
            .report-table th {
              background-color: #e9ecef !important;
              color: #212529 !important;
              padding: 6px 8px;
              text-align: center;
              font-weight: bold;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              border: 1px solid #ccc !important;
            }
            
            .report-table td {
              padding: 6px 8px;
              border: 1px solid #ccc !important;
              font-size: 12px;
              vertical-align: middle;
            }
            
            .report-table tr.even-row {
              background-color: #f8f9fa;
            }
            
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .text-left { text-align: left; }
            
            .badge {
              padding: 4px 6px;
              border-radius: 4px;
              font-size: 11px;
              font-weight: 600;
            }
            
            .badge-success {
              background-color: #f8f9fa !important;
              color: #495057 !important;
            }
            
            .badge-danger {
              background-color: #f8f9fa !important;
              color: #495057 !important;
            }
            
            .print-controls {
              position: fixed;
              top: 20px;
              right: 20px;
              z-index: 1000;
            }
            
            .print-btn {
              background: #6c757d;
              color: white;
              border: none;
              padding: 10px 16px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 13px;
              font-weight: bold;
              box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
              transition: all 0.3s ease;
            }
            
            .print-btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(108, 117, 125, 0.4);
            }
            
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              
              @page {
                margin: 0.5in !important;
                size: A4 !important;
              }
              
              html, body {
                margin: 0 !important;
                padding: 0 !important;
                background: white !important;
                width: 100% !important;
                height: 100% !important;
                font-size: 11px !important;
              }
              
              .print-controls {
                display: none !important;
              }
              
              .print-header {
                background: #f8f9fa !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .report-table th {
                background-color: #e9ecef !important;
                color: #212529 !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .badge-success {
                background-color: #f8f9fa !important;
                color: #495057 !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .badge-danger {
                background-color: #f8f9fa !important;
                color: #495057 !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              @page {
                size: A4 landscape;
                margin: 0.5in;
                background: white !important;
              }
            }
          </style>
        </head>
        <body>
          <div style="background: white !important; width: 100%; min-height: 100vh; margin: 0; padding: 0;">
            <!-- Print Controls -->
            <div class="print-controls">
              <button id="printButton" class="print-btn">
                🖨️ Print Report
              </button>
            </div>
            
            <!-- Header -->
            <div class="print-header">
              <h1>Medical Records Report</h1>
              <p>Generated on ${new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}</p>
          </div>
          
          
          <!-- Main Table -->
          ${allTableRows}
          
          </div>
        </body>
        </html>
      `;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

.btn-icon {
  width: 32px;
  height: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.btn-icon:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.font-weight-bold {
  font-weight: 600;
}

.health-assessments-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: visible;
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  z-index: 1;

  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem;
    right: 0.75rem;
    width: calc(100% - 1.5rem);
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }

  .filter-row-single-line {
    background: transparent !important;
    padding: 0;
    margin-bottom: 1rem;
  }
}

.table-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }

  small {
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: 500;
  }
}

.filter-row-single-line {
  gap: 0.5rem;
}

.no-wrap-row {
  flex-wrap: nowrap;

  @media (max-width: 991.98px) {
    // lg breakpoint
    flex-wrap: wrap;
  }
}

.filter-control {
  height: 38px;
  border-radius: 8px;
  border: 1px solid #e0d8ff;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
  }
}

.action-btn-filter {
  height: 38px;
  border-radius: 10px;
  padding: 0 1rem;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
  }
}

.print-btn-icon {
  width: 38px;
  height: 38px;
  padding: 0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0d8ff;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f7ff;
    border-color: #7c5dfa;
    transform: scale(1.05);
  }
}

.action-buttons-group {
  gap: 0.5rem;
  flex-wrap: wrap;
}

.soft-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e8e5fb;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.08);
  margin-bottom: 0.75rem;
}

.input-compact {
  height: 40px !important;
  border-radius: 10px !important;
  border: 1.5px solid #e0d8ff !important;
  padding: 0.45rem 0.75rem !important;
  font-size: 0.95rem !important;

  &:focus {
    border-color: #7c5dfa !important;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.12) !important;
  }
}

.compact-input-group .input-group-text {
  border-radius: 0 10px 10px 0 !important;
  border: 1.5px solid #e0d8ff !important;
  border-left: 0 !important;
  font-weight: 700;
  color: #6b7280;
}

.compact-input-group .form-control {
  border-radius: 10px 0 0 10px !important;
  border-right: 0 !important;
}

/* Fix double-border on v-select fields when using input-compact */
::v-deep .input-compact .vs__dropdown-toggle {
  border: 1.5px solid #e0d8ff !important;
  border-radius: 10px !important;
  min-height: 40px !important;
  height: 40px !important;
  box-shadow: none !important;
  padding: 0 0.75rem !important;
  background: #fff !important;
  background-image: none !important;
  outline: none !important;
}

::v-deep .input-compact .vs__selected,
::v-deep .input-compact .vs__search {
  padding: 0 !important;
  margin: 0 !important;
  line-height: 1.4;
}

::v-deep .input-compact .vs__dropdown-toggle:focus-within {
  border-color: #7c5dfa !important;
  box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.12) !important;
  outline: none !important;
}

::v-deep .input-compact .vs__actions {
  padding-right: 0.35rem !important;
}

/* Ensure only a single outline on v-select root */
::v-deep .input-compact.v-select {
  border: none !important;
  box-shadow: none !important;
}

/* Compact select styling to mirror HealthAssessment/LeaveRequest dropdowns */
.select-compact {
  ::v-deep .vs__dropdown-toggle {
    border: 1.5px solid #e0d8ff !important;
    border-radius: 10px !important;
    height: 40px !important;
    min-height: 40px !important;
    padding: 0 0.75rem !important;
    background: #fff !important;
    box-shadow: none !important;
    display: flex !important;
    align-items: center !important;
  }

  ::v-deep .vs__selected-options {
    padding: 0 !important;
    margin: 0 !important;
    align-items: center !important;
  }

  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 600 !important;
    line-height: 1.2 !important;
  }

  ::v-deep .vs__search {
    margin: 0 !important;
    padding: 0 !important;
    background: transparent !important;
  }

  ::v-deep .vs__actions {
    padding-right: 0.4rem !important;
    align-items: center !important;
  }

  ::v-deep .vs__dropdown-menu {
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 8px 18px rgba(115, 103, 240, 0.14) !important;
    border-radius: 10px !important;
    margin-top: 6px !important;
    background: #fff !important;
  }
}

/* Round default v-select borders in the modal to match inputs */
.form-card ::v-deep .v-select .vs__dropdown-toggle {
  border-radius: 10px !important;
  border: 1.5px solid #e0d8ff !important;
}

.pagination-controls-top {
  padding: 0.75rem 0;
  border-top: 1px solid #f0edff;
  border-bottom: 1px solid #f0edff;
  margin: 1rem 0;
  flex-wrap: wrap;
  gap: 1rem;
}

.pagination-switch-compact {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ::v-deep .custom-control-label {
    font-size: 0.8125rem;
    color: #6b7280;
    cursor: pointer;
  }
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-size-label {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
  white-space: nowrap;
}

.page-size-select-compact {
  min-width: 80px;

  ::v-deep .vs__dropdown-toggle {
    height: 32px;
    padding: 0 0.5rem;
    border-radius: 6px;
    border: 1px solid #e0d8ff;
    font-size: 0.8125rem;
  }
}

.pagination-count-display {
  flex: 1;
  text-align: center;

  .count-text {
    font-size: 0.8125rem;
    color: #6b7280;

    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }
}

.pagination-wrapper-compact {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-compact {
  margin: 0;

  ::v-deep .page-item {
    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      border-radius: 6px;
      border: 1px solid #e0d8ff;
      color: #6b7280;
      margin: 0 0.125rem;
      transition: all 0.2s ease;

      &:hover {
        background: #f8f7ff;
        border-color: #7c5dfa;
        color: #7c5dfa;
      }
    }

    &.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #ffffff;
    }
  }
}

.table-scroll-container {
  position: relative;
  width: 100%;
}

.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;

  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }
}

.table-scrollbar-top .table-scrollbar-content {
  height: 1px;
  width: 100%;
}

.table-wrapper {
  position: relative;
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;

  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }
}

.health-assessments-table {
  margin-top: 1rem;
  border-radius: 12px;
  overflow: hidden;
  min-width: 100%;
  width: max-content;
  border: 1px solid #e5e7eb;

  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 5;
  }

  ::v-deep td {
    padding: 0.875rem 0.875rem;
    border-bottom: 1px solid #f1f5f9;
    font-size: 0.8125rem;
    color: #475569;
    vertical-align: middle;
  }

  th,
  td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 8px 6px;
    font-size: 13px;
  }

  ::v-deep tbody tr {
    transition: all 0.2s ease;
    border-left: 3px solid transparent;

    &:hover {
      background: linear-gradient(90deg, #f8fafc 0%, #ffffff 100%);
      border-left-color: #7c5dfa;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.08);
      transform: translateX(2px);
    }
  }
}

.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
}

.action-btn-outline {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: #ffffff !important;
  border: 2px solid #ef4444 !important;
  color: #ef4444 !important;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.15);

  &:hover:not(:disabled) {
    background: #fef2f2 !important;
    border-color: #dc2626 !important;
    color: #dc2626 !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

.skeleton-row {
  animation: skeletonPulse 2s ease-in-out infinite;
  transition: opacity 0.3s ease;
}

.skeleton-cell {
  padding: 0.875rem 0.875rem !important;
  vertical-align: middle !important;
  border-top: 1px solid #f1f5f9 !important;
  border-bottom: 1px solid #f8fafc !important;
  background: #ffffff !important;
  position: relative;
}

.skeleton-loader {
  height: 18px;
  border-radius: 10px;
  background: linear-gradient(90deg, #f0f0f0 0%, #f8f8f8 50%, #f0f0f0 100%);
  background-size: 300% 100%;
  animation: skeletonShimmer 1.6s ease-in-out infinite;
  width: 100%;
}

.skeleton-cell:nth-child(1) .skeleton-loader {
  width: 50px;
  margin: 0 auto;
}
.skeleton-cell:nth-child(2) .skeleton-loader {
  width: 120px;
}
.skeleton-cell:nth-child(3) .skeleton-loader {
  width: 160px;
}
.skeleton-cell:nth-child(4) .skeleton-loader {
  width: 120px;
}
.skeleton-cell:nth-child(5) .skeleton-loader {
  width: 160px;
}
.skeleton-cell:nth-child(6) .skeleton-loader {
  width: 140px;
}
.skeleton-cell:nth-child(7) .skeleton-loader {
  width: 120px;
}
.skeleton-cell:nth-child(8) .skeleton-loader {
  width: 100px;
}
.skeleton-cell:nth-child(9) .skeleton-loader {
  width: 120px;
}
.skeleton-cell:nth-child(10) .skeleton-loader {
  width: 80px;
}

@keyframes skeletonPulse {
  0% {
    opacity: 0.9;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    opacity: 0.9;
  }
}

@keyframes skeletonShimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

.view-modal-content {
  font-size: 0.875rem;
}

.view-info-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 14px;
  padding: 1rem 1.25rem;
  box-shadow: 0 6px 18px rgba(115, 103, 240, 0.08);
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.info-name {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.info-name h5 {
  font-weight: 800;
  color: #2d1b69;
  margin: 0;
}

.info-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.status-chip {
  font-weight: 700;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  text-transform: capitalize;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.view-section {
  margin-bottom: 1.25rem;
}

.section-header h6 {
  font-weight: 800;
  color: #2d1b69;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.info-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e8e5fb;
  border-radius: 12px;
  padding: 0.9rem 1rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.08);
  min-height: 120px;
}

.metric-label {
  display: block;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #8a86b7;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.info-text {
  margin: 0.35rem 0 0;
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.5;
}

// Student Option Styles (from circulars.vue)
.student-option-item {
  padding: 10px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  position: relative;

  &:hover {
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    border-color: #28a745;
  }
}

.student-main-info {
  width: 100%;
}

.student-name-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.student-name {
  color: #1a237e;
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  line-height: 1.2;
  transition: color 0.2s ease;
}

.student-gr {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #1976d2;
  font-size: 11px;
  padding: 3px 8px;
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  border-radius: 6px;
  border: 1px solid #2196f3;
  transition: all 0.2s ease;
  white-space: nowrap;

  .detail-icon {
    color: #1976d2;
    flex-shrink: 0;
  }

  strong {
    color: #0d47a1;
    font-weight: 600;
  }
}

.student-gender {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 500;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &.male {
    background: linear-gradient(135deg, #1976d2, #42a5f5);
    color: white;
  }

  &.female {
    background: linear-gradient(135deg, #c2185b, #e91e63);
    color: white;
  }

  .detail-icon {
    color: white;
    transition: color 0.2s ease;
  }
}

.student-details-compact {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.detail-item-compact {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #424242;
  font-size: 11px;
  padding: 3px 8px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: white;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  .detail-icon {
    color: #28a745;
    flex-shrink: 0;
    transition: color 0.2s ease;
  }

  strong {
    color: #2c3e50;
    font-weight: 600;
    transition: color 0.2s ease;
  }
}

// Enhanced Student Select Styles
.enhanced-student-select {
  .vs__dropdown-menu {
    max-height: 350px;
    overflow-y: auto;
  }

  // Fix selected/hover states
  .vs__dropdown-option {
    &.vs__dropdown-option--highlight {
      background: linear-gradient(135deg, #667eea, #764ba2) !important;
      color: #1a1a1a !important;

      .student-name {
        color: #1a1a1a !important;
        text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
        font-weight: 700;
      }

      .detail-item-compact {
        background: rgba(255, 255, 255, 0.9) !important;
        border-color: rgba(103, 126, 234, 0.3) !important;
        color: #2c3e50 !important;

        .detail-icon {
          color: #28a745 !important;
        }

        strong {
          color: #1a237e !important;
        }
      }

      .student-gr {
        background: rgba(255, 255, 255, 0.95) !important;
        border-color: #2196f3 !important;
        color: #1565c0 !important;

        .detail-icon {
          color: #1976d2 !important;
        }

        strong {
          color: #0d47a1 !important;
        }
      }

      .student-gender {
        background: rgba(255, 255, 255, 0.9) !important;
        border: 1px solid rgba(103, 126, 234, 0.3) !important;
        color: #2c3e50 !important;

        .detail-icon {
          color: #1976d2 !important;
        }
      }
    }
  }
}

// Custom scrollbar for dropdown
.vs__dropdown-menu::-webkit-scrollbar {
  width: 6px;
}

.vs__dropdown-menu::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.vs__dropdown-menu::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;

  &:hover {
    background: #a8a8a8;
  }
}

// New search functionality styles
.search-section {
  margin-bottom: 16px;
}

.search-input {
  border-radius: 6px;
  border: 1px solid #ced4da;
  padding: 8px 12px;
  font-size: 14px;
  width: 100%;
}

.search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.search-results {
  position: relative;
  z-index: 1000;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  margin-top: 4px;
  /* Remove double scrollbar issue */
  overflow-x: hidden;
}

.student-options {
  /* Remove nested scrolling to prevent double scrollbars */
  max-height: none;
  overflow: visible;
}

.student-option {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  /* Ensure no internal scrolling */
  overflow: visible;
}

.student-option:hover {
  background-color: #f8f9fa;
  border-left: 3px solid #007bff;
  transform: translateX(2px);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
}

.student-option:last-child {
  border-bottom: none;
}

.student-option:active {
  transform: translateX(1px);
  background-color: #e3f2fd;
}

.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #6c757d;
}

.empty-state p {
  margin-top: 12px;
  font-size: 14px;
}

.loading-state {
  padding: 20px;
  text-align: center;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.selected-student {
  margin-top: 12px;
  padding: 12px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

// GR Number styling
.ml-2 {
  margin-left: 8px;
}

.mr-1 {
  margin-right: 4px;
}

.text-muted small .ml-2 {
  color: #6c757d;
  font-size: 12px;
  font-weight: 500;
}

// Custom scrollbar for search results - smooth and modern
.search-results::-webkit-scrollbar {
  width: 6px;
}

.search-results::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.search-results::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
  transition: background 0.2s ease;
}

.search-results::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

// Ensure no double scrollbars
.search-results * {
  overflow: visible !important;
}

.student-options * {
  overflow: visible !important;
}
</style>
